import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Ban, Banknote, CalendarDays, ClipboardList, CreditCard, PoundSterling,
  RefreshCw, ShoppingBag, Store, TicketPercent, TrendingUp, Trophy, Truck, XCircle
} from 'lucide-react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api } from '../../services/api';
import './AdminUI.css';
import AdminTopbarPortal from './AdminTopbarPortal';

const isoLocalDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const todayValue = () => isoLocalDate(new Date());

const dashboardDateParams = (mode, rangeStart, rangeEnd, currentTradingDate) => {
  // Omitting dates for Today lets the backend resolve the tenant's actual
  // trading day, including an overnight service that crosses midnight.
  if (mode === 'today') return {};
  const today = currentTradingDate
    ? new Date(`${currentTradingDate}T12:00:00`)
    : new Date();
  const todayString = currentTradingDate || isoLocalDate(today);
  if (mode === 'yesterday') {
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    const value = isoLocalDate(yesterday);
    return { fromDate: value, toDate: value };
  }
  if (mode === 'week') {
    const monday = new Date(today);
    const weekday = monday.getDay() || 7;
    monday.setDate(monday.getDate() - weekday + 1);
    return { fromDate: isoLocalDate(monday), toDate: todayString };
  }
  if (mode === 'month') return { fromDate: `${todayString.slice(0, 7)}-01`, toDate: todayString };
  if (mode === 'year') return { fromDate: `${today.getFullYear()}-01-01`, toDate: todayString };
  return {
    fromDate: rangeStart || todayString,
    toDate: rangeEnd || rangeStart || todayString
  };
};

const formatMoney = value => new Intl.NumberFormat('en-GB', {
  style: 'currency', currency: 'GBP'
}).format(Number(value || 0));

const METRICS = [
  { key: 'totalOrders', label: 'Total orders', icon: ClipboardList },
  { key: 'pickupOrders', label: 'Total pickup', icon: ShoppingBag },
  { key: 'deliveryOrders', label: 'Total delivery', icon: Truck },
  { key: 'cancelledOrders', label: 'Cancelled', icon: XCircle, outcome: true },
  { key: 'rejectedOrders', label: 'Rejected', icon: Ban, outcome: true },
  { key: 'cardRevenue', label: 'Total card', icon: CreditCard, money: true },
  { key: 'cashRevenue', label: 'Total cash', icon: Banknote, money: true },
  { key: 'totalRevenue', label: 'Total revenue', icon: PoundSterling, money: true },
  { key: 'walkInOrders', label: 'Total walk-in', icon: Store },
  { key: 'posCardRevenue', label: 'POS card', icon: CreditCard, money: true },
  { key: 'posCashRevenue', label: 'POS cash', icon: Banknote, money: true }
];

function MetricCard({ metric, value }) {
  const Icon = metric.icon;
  return (
    <div className={`admin-dashboard-metric${metric.outcome ? ' is-outcome' : ''}`}>
      <div className="admin-dashboard-metric-icon"><Icon /></div>
      <div>
        <span>{metric.label}</span>
        <strong>{metric.money ? formatMoney(value) : Number(value || 0).toLocaleString('en-GB')}</strong>
      </div>
    </div>
  );
}

function RevenueLineChart({ series }) {
  return (
    <div className="admin-dashboard-line-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 12, right: 18, bottom: 4, left: 2 }}>
          <CartesianGrid vertical={false} stroke="#eaecf0" />
          <XAxis dataKey="label" tick={{ fill: '#667085', fontSize: 10 }} tickLine={false} axisLine={false} minTickGap={24} />
          <YAxis tickFormatter={value => `£${Number(value).toLocaleString('en-GB')}`} tick={{ fill: '#667085', fontSize: 10 }} tickLine={false} axisLine={false} width={58} />
          <Tooltip
            formatter={(value, name, details) => [formatMoney(value), `${details?.payload?.orders || 0} orders`]}
            labelStyle={{ color: '#101828', fontWeight: 600 }}
            contentStyle={{ border: '1px solid #e4e7ec', borderRadius: 8, boxShadow: '0 8px 24px rgb(16 24 40 / .1)', fontSize: 11 }}
          />
          <Line type="monotone" dataKey="revenue" stroke="#072929" strokeWidth={3} dot={{ r: 3, fill: '#fff', stroke: '#072929', strokeWidth: 2 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function AdminDashboard() {
  const [filterMode, setFilterMode] = useState('today');
  const [rangeStart, setRangeStart] = useState(todayValue);
  const [rangeEnd, setRangeEnd] = useState(todayValue);
  const [data, setData] = useState(null);
  const [currentTradingDate, setCurrentTradingDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestNumber = useRef(0);

  const params = useMemo(
    () => dashboardDateParams(filterMode, rangeStart, rangeEnd, currentTradingDate),
    [filterMode, rangeStart, rangeEnd, currentTradingDate]
  );

  const load = async () => {
    const currentRequest = ++requestNumber.current;
    setLoading(true);
    setError('');
    try {
      const result = await api.getAdminDashboard(params);
      if (currentRequest === requestNumber.current) {
        setData(result);
        if (filterMode === 'today' && result?.fromDate) {
          setCurrentTradingDate(result.fromDate);
        }
      }
    } catch (err) {
      if (currentRequest === requestNumber.current) {
        setError(err.response?.data?.detail || 'Dashboard figures could not be loaded.');
      }
    } finally {
      if (currentRequest === requestNumber.current) setLoading(false);
    }
  };

  useEffect(() => { load(); }, [params.fromDate, params.toDate]);

  const summary = data?.summary || {};
  const series = data?.salesSeries || [];
  const topItems = data?.topItems || [];
  const couponBreakdown = data?.couponBreakdown || [];
  return (
    <div className="admin-view admin-dashboard">
      <AdminTopbarPortal>
        <div className="admin-topbar-controls">
          <div className="admin-toolbar admin-dashboard-filters">
            <CalendarDays aria-hidden="true" />
            <select value={filterMode} onChange={event => setFilterMode(event.target.value)} aria-label="Dashboard reporting period">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">This week</option>
              <option value="month">This month</option>
              <option value="year">Year to date</option>
              <option value="custom">Custom range</option>
            </select>
            {filterMode === 'custom' && (
              <>
                <input type="date" value={rangeStart} max={rangeEnd || undefined} onChange={event => setRangeStart(event.target.value)} aria-label="Dashboard period start" />
                <span className="admin-date-filter-separator">to</span>
                <input type="date" value={rangeEnd} min={rangeStart || undefined} onChange={event => setRangeEnd(event.target.value)} aria-label="Dashboard period end" />
              </>
            )}
          </div>
          <button type="button" onClick={load} disabled={loading} className="admin-secondary-button">
            <RefreshCw className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </AdminTopbarPortal>

      {error && (
        <div className="admin-dashboard-error">
          <Ban /><div><strong>Dashboard unavailable</strong><span>{error}</span></div>
        </div>
      )}

      <div className="admin-dashboard-period">
        <div><Store /><span><strong>Trading period</strong>{data?.periodLabel || 'Loading configured opening hours…'}</span></div>
      </div>

      <div className={`admin-dashboard-content${loading ? ' is-loading' : ''}`}>
        <section className="admin-dashboard-metrics" aria-label="Sales summary">
          {METRICS.map(metric => <MetricCard key={metric.key} metric={metric} value={summary[metric.key]} />)}
        </section>

        <section className="admin-dashboard-panel admin-dashboard-revenue-panel">
          <div className="admin-dashboard-section-heading">
            <div><span>Performance</span><h2>Revenue activity</h2><p>Valid orders within the selected trading period.</p></div>
            <TrendingUp />
          </div>
          {series.length ? <RevenueLineChart series={series} /> : <div className="admin-dashboard-empty">No qualifying sales in this trading period.</div>}
        </section>

        <div className="admin-dashboard-bottom-grid">
          <section className="admin-dashboard-panel admin-dashboard-top-items">
          <div className="admin-dashboard-section-heading">
            <div><h2>Top 10 selling items</h2><p>Ranked by item quantity across qualifying orders.</p></div>
            <Trophy />
          </div>
          {topItems.length ? (
            <div className="admin-dashboard-table-shell">
              <table>
                <thead><tr><th>Rank</th><th>Menu item</th><th>Units sold</th><th>Item sales</th></tr></thead>
                <tbody>{topItems.map((item, index) => (
                  <tr key={`${item.name}-${index}`}>
                    <td data-label="Rank"><span className="admin-dashboard-rank">{index + 1}</span></td>
                    <td data-label="Menu item"><strong>{item.name}</strong></td>
                    <td data-label="Units sold">{Number(item.quantity || 0).toLocaleString('en-GB')}</td>
                    <td data-label="Item sales"><strong>{formatMoney(item.revenue)}</strong></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          ) : <div className="admin-dashboard-empty">No selling items to show for this trading period.</div>}
          </section>

          <section className="admin-dashboard-panel admin-dashboard-coupons">
            <div className="admin-dashboard-section-heading">
              <div><h2>Coupon usage</h2><p>Discounts applied to qualifying orders.</p></div>
              <TicketPercent />
            </div>
            <div className="admin-dashboard-coupon-metrics">
              <div><span>Coupons used</span><strong>{Number(summary.couponsUsed || 0).toLocaleString('en-GB')}</strong></div>
              <div><span>Total discount</span><strong>{formatMoney(summary.totalDiscount)}</strong></div>
            </div>
            {couponBreakdown.length ? (
              <div className="admin-dashboard-coupon-list">
                <div className="admin-dashboard-coupon-list-header"><span>Coupon</span><span>Uses</span><span>Discount</span></div>
                {couponBreakdown.map(coupon => (
                  <div className="admin-dashboard-coupon-row" key={coupon.code}>
                    <div><strong>{coupon.name}</strong><code>{coupon.code}</code></div>
                    <span>{Number(coupon.uses || 0).toLocaleString('en-GB')}</span>
                    <strong>{formatMoney(coupon.totalDiscount)}</strong>
                  </div>
                ))}
              </div>
            ) : <div className="admin-dashboard-coupon-empty">No coupons used in this trading period.</div>}
          </section>
        </div>
      </div>

      {loading && data && <div className="admin-dashboard-updating"><RefreshCw className="animate-spin" /> Updating dashboard…</div>}
      {loading && !data && <div className="admin-dashboard-initial-loading"><RefreshCw className="animate-spin" /> Loading dashboard…</div>}
    </div>
  );
}
